/**
 * A team's spaces as cards (opening one leaves for the editor), and, for editors and owners, a
 * card to make a new one (which then opens).
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { TeamView } from "@protocol/TeamView";
import { atLeast } from "../state/access";
import { formatDate } from "../shared/ui";
import { usePages } from "./context";

/** The spaces of `team`. */
export function SpacesGrid(props: { team: TeamView }): JSX.Element {
  const { accounts, server } = usePages();
  const [name, setName] = createSignal("");
  /** Makes the space and opens it in the editor. */
  const create = async () => {
    const space = await accounts.createSpace(server, props.team.id, name());
    if (space) location.assign(`/s/${space.id}${location.search}`);
  };
  return (
    <section class="team-section">
      <h3>Spaces</h3>
      <div class="space-grid">
        <For each={props.team.spaces}>
          {(space) => (
            <a class="space-card" data-space={space.id} href={`/s/${space.id}${location.search}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                <circle cx="6" cy="7" r="2.4" />
                <circle cx="18" cy="6" r="2.4" />
                <circle cx="12" cy="18" r="2.4" />
                <path d="M8 8l3 8M16.4 8L13 16M8.4 7h7.2" />
              </svg>
              <b>{space.name}</b>
              <span class="muted small">made {formatDate(space.created)}</span>
            </a>
          )}
        </For>
        <Show when={atLeast(props.team.role, "editor")}>
          <form
            class="space-card new"
            id="new-space"
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            <b>New space</b>
            <input class="field" id="new-space-name" placeholder="Space name" required value={name()} onInput={(e) => setName(e.currentTarget.value)} />
            <button type="submit" class="btn primary" id="new-space-create">
              Create and open
            </button>
          </form>
        </Show>
      </div>
      <Show when={props.team.spaces.length === 0 && !atLeast(props.team.role, "editor")}>
        <p class="muted">No space yet: an editor of the team makes them.</p>
      </Show>
    </section>
  );
}
